import type {
    PolicyDocument,
    PolicyElement,
    ConstantElement,
    SingleValueRuleElement,
    SetRuleElement,
    ObjectRuleElement,
    FunctionElement,
    RawRegoElement,
    RegoValue,
    Condition,
    ElseClause,
} from '@/types/policy-editor';

const LOG_PREFIX = 'rego-generator';
void LOG_PREFIX;

const INDENT = '    ';

export function generateRego(doc: PolicyDocument): string {
    const sections: string[] = [];

    sections.push(`package ${doc.packagePath}`);

    if (doc.imports.length > 0) {
        const importLines = doc.imports.map((imp) =>
            imp.alias ? `import ${imp.path} as ${imp.alias}` : `import ${imp.path}`,
        );
        sections.push(importLines.join('\n'));
    }

    for (const el of doc.elements) {
        sections.push(renderElement(el));
    }

    return sections.join('\n\n') + '\n';
}

export function renderElement(el: PolicyElement): string {
    const lines: string[] = [];

    if (el.description) {
        for (const line of el.description.split('\n')) {
            lines.push(`# ${line}`);
        }
    }

    switch (el.elementType) {
        case 'constant':
            lines.push(renderConstant(el));
            break;
        case 'singleValueRule':
            lines.push(renderSingleValueRule(el));
            break;
        case 'setRule':
            lines.push(renderSetRule(el));
            break;
        case 'objectRule':
            lines.push(renderObjectRule(el));
            break;
        case 'function':
            lines.push(renderFunction(el));
            break;
        case 'rawRego':
            lines.push(renderRawRego(el));
            break;
    }

    return lines.join('\n');
}

function renderConstant(el: ConstantElement): string {
    const prefix = el.isDefault ? 'default ' : '';
    const op = el.isDefault ? ' :=' : ' =';
    return `${prefix}${el.name}${op} ${serializeValue(el.value)}`;
}

function renderSingleValueRule(el: SingleValueRuleElement): string {
    const parts: string[] = [];

    if (el.isDefault) {
        parts.push(`default ${el.name} := ${serializeValue(el.value)}`);
        return parts.join('\n');
    }

    const hasElse = el.elseChain && el.elseChain.length > 0;

    const isBoolTrue = el.value.kind === 'scalar' && el.value.value === true;

    if (el.conditions.length === 0) {
        if (isBoolTrue) {
            parts.push(`${el.name} := true`);
        } else {
            parts.push(`${el.name} = ${serializeValue(el.value)}`);
        }
    } else {
        if (isBoolTrue) {
            parts.push(`${el.name} if {`);
        } else {
            parts.push(`${el.name} = ${serializeValue(el.value)} if {`);
        }
        parts.push(...renderConditionBody(el.conditions));
        if (!hasElse) {
            parts.push('}');
        }
    }

    if (hasElse) {
        const chain = el.elseChain!;
        for (let i = 0; i < chain.length; i++) {
            const clause = chain[i];
            const isLast = i === chain.length - 1;
            parts.push(...renderElseClause(clause, isLast));
        }
    }

    return parts.join('\n');
}

function renderElseClause(clause: ElseClause, isLast: boolean): string[] {
    const lines: string[] = [];
    if (clause.conditions.length === 0) {
        lines.push(`} else = ${serializeValue(clause.value)} if {`);
        lines.push(`${INDENT}true`);
    } else {
        lines.push(`} else = ${serializeValue(clause.value)} if {`);
        lines.push(...renderConditionBody(clause.conditions));
    }
    if (isLast) {
        lines.push('}');
    }
    return lines;
}

function renderSetRule(el: SetRuleElement): string {
    const parts: string[] = [];
    if (el.conditions.length === 0) {
        parts.push(`${el.name} contains ${el.memberExpression}`);
    } else {
        parts.push(`${el.name} contains ${el.memberExpression} if {`);
        parts.push(...renderConditionBody(el.conditions));
        parts.push('}');
    }
    return parts.join('\n');
}

function renderObjectRule(el: ObjectRuleElement): string {
    const parts: string[] = [];
    if (el.conditions.length === 0) {
        parts.push(`${el.name}[${el.keyExpression}] = ${el.valueExpression}`);
    } else {
        parts.push(`${el.name}[${el.keyExpression}] = ${el.valueExpression} if {`);
        parts.push(...renderConditionBody(el.conditions));
        parts.push('}');
    }
    return parts.join('\n');
}

function renderFunction(el: FunctionElement): string {
    const params = el.parameters.join(', ');
    const parts: string[] = [];
    if (el.conditions.length === 0) {
        parts.push(`${el.name}(${params}) = ${el.returnValue}`);
    } else {
        parts.push(`${el.name}(${params}) = ${el.returnValue} if {`);
        parts.push(...renderConditionBody(el.conditions));
        parts.push('}');
    }
    return parts.join('\n');
}

function renderRawRego(el: RawRegoElement): string {
    return el.source;
}

function renderConditionBody(conditions: Condition[], indent: string = INDENT): string[] {
    const lines: string[] = [];
    for (const c of conditions) {
        if (c.type === 'universal') {
            lines.push(...renderUniversalBlock(c, indent));
        } else {
            lines.push(`${indent}${serializeCondition(c)}`);
        }
    }
    return lines;
}

function serializeCondition(cond: Condition): string {
    switch (cond.type) {
        case 'comparison':
            return `${cond.left} ${cond.operator} ${cond.right}`;
        case 'membership':
            if (cond.negated) {
                return `not ${cond.term} in ${cond.collection}`;
            }
            return `${cond.term} in ${cond.collection}`;
        case 'assignment':
            return `${cond.variable} := ${cond.expression}`;
        case 'iteration':
            if (cond.variables.length === 1) {
                return `some ${cond.variables[0]} in ${cond.collection}`;
            }
            return `some ${cond.variables.join(', ')} in ${cond.collection}`;
        case 'universal':
            return renderUniversal(cond);
        case 'negation':
            return `not ${cond.expression}`;
        case 'functionCall':
            return renderFunctionCallCondition(cond);
        case 'raw':
            return cond.text;
    }
}

function renderUniversalBlock(cond: Extract<Condition, { type: 'universal' }>, indent: string): string[] {
    const vars = cond.variables.join(', ');
    const innerIndent = indent + INDENT;

    if (cond.body.length === 1 && cond.body[0].type !== 'universal') {
        return [`${indent}every ${vars} in ${cond.collection} { ${serializeCondition(cond.body[0])} }`];
    }

    const lines: string[] = [];
    lines.push(`${indent}every ${vars} in ${cond.collection} {`);
    lines.push(...renderConditionBody(cond.body, innerIndent));
    lines.push(`${indent}}`);
    return lines;
}

function renderUniversal(cond: Extract<Condition, { type: 'universal' }>): string {
    return renderUniversalBlock(cond, '').join('\n');
}

function renderFunctionCallCondition(cond: Extract<Condition, { type: 'functionCall' }>): string {
    const call = `${cond.name}(${cond.args.join(', ')})`;
    if (cond.assignTo) {
        return `${cond.assignTo} := ${call}`;
    }
    return call;
}

function serializeValue(val: RegoValue): string {
    switch (val.kind) {
        case 'scalar':
            return serializeScalar(val.value);
        case 'object':
            return serializeObject(val);
        case 'array':
            return serializeArray(val);
        case 'set':
            return serializeSet(val);
        case 'reference':
            return val.path;
        case 'functionCall':
            return `${val.name}(${val.args.map(serializeValue).join(', ')})`;
        case 'comprehension':
            return serializeComprehension(val);
        case 'raw':
            return val.text;
    }
}

function serializeScalar(value: string | number | boolean | null): string {
    if (value === null) return 'null';
    if (typeof value === 'string') return JSON.stringify(value);
    return String(value);
}

function serializeObject(val: Extract<RegoValue, { kind: 'object' }>): string {
    if (val.entries.length === 0) return '{}';
    const pairs = val.entries.map(
        (e) => `${JSON.stringify(e.key)}: ${serializeValue(e.value)}`,
    );
    if (pairs.length <= 3 && pairs.join(', ').length < 60) {
        return `{${pairs.join(', ')}}`;
    }
    const inner = pairs.map((p) => `${INDENT}${p},`).join('\n');
    return `{\n${inner}\n}`;
}

function serializeArray(val: Extract<RegoValue, { kind: 'array' }>): string {
    if (val.items.length === 0) return '[]';
    const items = val.items.map(serializeValue);
    const inline = items.join(', ');
    if (items.length <= 5 && inline.length < 60) {
        return `[${inline}]`;
    }
    const inner = items.map((i) => `${INDENT}${i},`).join('\n');
    return `[\n${inner}\n]`;
}

function serializeSet(val: Extract<RegoValue, { kind: 'set' }>): string {
    if (val.items.length === 0) return 'set()';
    const items = val.items.map(serializeValue);
    const inline = items.join(', ');
    if (items.length <= 5 && inline.length < 60) {
        return `{${inline}}`;
    }
    const inner = items.map((i) => `${INDENT}${i},`).join('\n');
    return `{\n${inner}\n}`;
}

function serializeComprehension(val: Extract<RegoValue, { kind: 'comprehension' }>): string {
    switch (val.type) {
        case 'array':
            return `[${val.term} | ${val.body}]`;
        case 'set':
            return `{${val.term} | ${val.body}}`;
        case 'object':
            return `{${val.keyTerm}: ${val.term} | ${val.body}}`;
    }
}
